// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { getIsLeadCapture } from "@werner/App/Store/Selectors/ProductSelectors";
import { XNGProductAvailabilityProps } from "@werner/Overrides/Components/ProductAvailability";
import { parseBoolean } from "@werner/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends XNGProductAvailabilityProps {
    isProductDetailsPage?: boolean;
    configurationCompleted?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => {
    return {
        includeRealTimeInventoryAndPricingForSeo:
            getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo,
        // XNG-Changes
        isLeadCaputre: getIsLeadCapture(ownProps.product),
        isAuthenticated: xIsAuthenticated(state),
        isQuickOrderPage: getCurrentPage(state)?.type === "QuickOrderPage",
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProduct;

const ProductContextAvailability = ({
    includeRealTimeInventoryAndPricingForSeo,
    productInfo,
    product,
    isProductDetailsPage,
    configurationCompleted,
    extendedStyles,
    // XNG-Changes
    isLeadCaputre,
    isAuthenticated,
    isQuickOrderPage,
    ...productAvailabilityProps
}: Props) => {
    const { inventory, unitOfMeasure, failedToLoadInventory } = productInfo;

    const availability =
        inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;
    // XNG-Changes: add check for (isLeadCaputre && isAuthenticated)
    if (
        (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) ||
        product.isVariantParent ||
        (!!product.configurationType &&
            product.configurationType !== ConfigurationType.None &&
            product.configurationType !== ConfigurationType.Fixed &&
            !configurationCompleted)
    ) {
        return null;
    }

    if (isLeadCaputre && isAuthenticated) {
        <StyledWrapper
            css={css`
                min-height: 22px;
            `}
        ></StyledWrapper>;
    }

    return (
        <ProductAvailability
            // XNG-Changes
            {...productAvailabilityProps}
            productId={product.id}
            trackInventory={product.trackInventory}
            availability={availability}
            unitOfMeasure={unitOfMeasure}
            isProductDetailsPage={isProductDetailsPage}
            failedToLoadInventory={failedToLoadInventory}
            extendedStyles={extendedStyles}
            {...{
                pickupMessage: isProductDetailsPage ? productInfo.inventory?.additionalResults?.pickupMessage : "",
                // XNG-Change WES-1850
                hideOutOfStockMessage: parseBoolean(product.properties.hideOutofStockMessage),
                leadTimeMessage: isQuickOrderPage
                    ? productAvailabilityProps.linkOnly
                        ? product.properties.leadTimeMessage
                        : ""
                    : product.properties.leadTimeMessage,
            }}
        />
    );
};

export default connect(mapStateToProps)(withProduct(ProductContextAvailability));
